import type { CSSInterpolation } from '@emotion/serialize';
import type { Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { MouseEvent, RefObject } from 'react';
import { useCallback } from 'react';

import { EdgeShadow } from '@adflow/frontend/ui-layout';
import { useClickIgnoreDrag } from '@adflow/frontend/util-common';

interface DataTableRowProps<T> {
	row: Row<T>;

	onRowClick?: (row: T, e?: MouseEvent<HTMLTableRowElement>) => void;

	rowStyle: CSSInterpolation;
	cellStyle: CSSInterpolation | ((args: { row: Row<T> }) => CSSInterpolation);
	showTopEdgeShadow: boolean;
	showBottomEdgeShadow: boolean;
	onlySticky?: boolean;

	rowRef?: RefObject<HTMLTableRowElement>;
}

export function DataTableRow<T>({
	row,
	onRowClick,
	rowStyle,
	cellStyle,
	showTopEdgeShadow,
	showBottomEdgeShadow,
	onlySticky,
	rowRef,
}: DataTableRowProps<T>) {
	const onRowClickProps = useClickIgnoreDrag<HTMLTableRowElement>(
		useCallback(
			(e) => {
				onRowClick?.(row.original, e);
			},
			[onRowClick, row.original]
		)
	);

	return (
		<tr role="row" css={rowStyle} {...onRowClickProps} ref={rowRef}>
			{row.getVisibleCells().map((cell) => {
				if (!cell.column.getIsPinned() && onlySticky) return null;

				return (
					<td
						key={cell.id}
						role="cell"
						css={{ padding: 0 }}
						style={{ width: cell.column.getSize() }}
					>
						<div
							style={{
								paddingLeft: cell.column.getPaddingLeft(),
								paddingRight: cell.column.getPaddingRight(),
								maxHeight: '3.5rem',
								overflow: 'hidden',
							}}
							css={[
								typeof cellStyle === 'function'
									? cellStyle({ row })
									: cellStyle,
								{
									maxHeight: '3.5rem',
									overflow: 'hidden',
								},
							]}
						>
							{flexRender(cell.column.columnDef.cell, cell.getContext())}
						</div>
					</td>
				);
			})}

			{showTopEdgeShadow ? <EdgeShadow side="top" as="td" /> : null}
			{showBottomEdgeShadow ? <EdgeShadow side="bottom" as="td" /> : null}
		</tr>
	);
}
